import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { ApiError } from '../../../../../shared/shared.api';
import { getTripMessages, markTripMessagesRead, sendTripMessage, type TripMessageView } from '../trip-messages.api';

export type TripMessagesState = { status: 'loading' } | { status: 'loaded'; messages: TripMessageView[] } | { status: 'error' };

const REFRESH_MS = 30_000;

/** Server text for a refusal, else a generic line. */
function refusal(err: unknown): string {
  const body = err instanceof ApiError ? err.body : null;
  const message = typeof body === 'object' && body !== null && 'message' in body ? (body as { message: unknown }).message : null;
  return typeof message === 'string' ? message : 'Could not send the message. Try again.';
}

/** The thread, re-read every 30s (there is no push to the office yet), marked read as far as it was shown, and the send. */
export function useTripMessages(tripId: string) {
  const [state, setState] = useState<TripMessagesState>({ status: 'loading' });
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reads, setReads] = useState(0);
  const markedUpTo = useRef<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    const read = () =>
      getTripMessages(tripId)
        .then(({ messages }) => {
          if (cancelled) return;
          setState({ status: 'loaded', messages });
          const newest = messages.at(-1);
          if (!newest || newest.id === markedUpTo.current) return;
          markedUpTo.current = newest.id;
          markTripMessagesRead(tripId, newest.id).catch(() => undefined);
        })
        .catch(() => {
          if (!cancelled) setState((current) => (current.status === 'loaded' ? current : { status: 'error' }));
        });
    void read();
    const timer = setInterval(read, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [tripId, reads]);

  const load = useCallback(() => setReads((count) => count + 1), []);
  const retry = useCallback(() => {
    setState({ status: 'loading' });
    load();
  }, [load]);

  async function send(body: string): Promise<boolean> {
    setIsSending(true);
    setError(null);
    try {
      await sendTripMessage(tripId, { body, clientMessageId: crypto.randomUUID() });
      toast.success('Message sent to the driver');
      load();
      return true;
    } catch (err) {
      setError(refusal(err));
      return false;
    } finally {
      setIsSending(false);
    }
  }

  return { state, retry, send, isSending, error };
}
